import React, { useEffect, useState } from "react";
import { Modal, Row, Col } from "react-bootstrap";
import {
  Button,
  Box,
  Typography,
  TextField,
  Switch,
  CircularProgress,
  Paper,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { API } from "../../../api/API";
import { toast } from "react-toastify";

const validationSchema = Yup.object().shape({
  intitule: Yup.string().required("L'intitulé est obligatoire"),

  rccm: Yup.string().required("Le RCCM est obligatoire"),
  ifu: Yup.string().required("L'IFU est obligatoire"),
  tel: Yup.string()
    .required("Le téléphone est obligatoire")
    .matches(/^[0-9]+$/, "Doit contenir uniquement des chiffres")
    .min(8, "Doit contenir au moins 8 chiffres"),
  email: Yup.string()
    .email("Email invalide")
    .required("L'email est obligatoire"),
  adresse: Yup.string().required("L'adresse est obligatoire"),
  isActive: Yup.boolean(),
});

const UpdatestructureModal = ({
  show,
  handleClose,
  structure,
  onUpdateSuccess,
}) => {
  const initialValues = {
    intitule: "",
    code: "",
    rccm: "",
    ifu: "",
    tel: "",
    adresse: "",
    email: "",
    isActive: false,
  };

  const [formValues, setFormValues] = useState(initialValues);
  const [isLoading, setIsLoading] = useState(false);

  const fetchStructureData = async () => {
    if (!structure) return;

    setIsLoading(true);
    try {
      const { id } = structure;
      const response = await API.get(`structures/${id}`);
      if (response.data?.data) {
        const data = response.data.data;
        setFormValues({
          intitule: data.intitule || "",

          rccm: data.rccm || "",
          ifu: data.ifu || "",
          tel: data.tel || "",
          adresse: data.adresse || "",
          email: data.email || "",
          isActive: data.isActive || false,
        });
      }
    } catch (error) {
      console.error("Error fetching structure data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (show && structure) {
      fetchStructureData();
    } else {
      setFormValues(initialValues);
    }
  }, [show, structure]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await API.put(`structures/${structure.id}`, values);
      toast.success("Informations mises à jour avec succès");
      onUpdateSuccess?.();
      handleClose();
    } catch (error) {
      console.error("Error updating structure:", error);
      toast.error(
        error.response?.data?.message || "Erreur lors de la mise à jour"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!show || !structure) return null;

  return (
    <Modal
      size="xl"
      show={show}
      onHide={handleClose}
      aria-labelledby="update-structure-modal"
      centered
      backdrop="static"
      className="custom-modal"
    >
      <Paper sx={{ p: 0, display: "flex", flexDirection: "column" }}>
        <Modal.Header closeButto className="bg-success text-white">
          <Typography
            variant="h5"
            component="h2"
            fontWeight="bold"
            color="white"
          >
            {isLoading ? "Chargement..." : `Modifier ${formValues.intitule}`}
          </Typography>
        </Modal.Header>

        <Modal.Body
          style={{
            overflowY: "auto",
            flex: 1,
            padding: "0 16px",
            margin: "16px 0",
          }}
        >
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <CircularProgress />
            </Box>
          ) : (
            <Formik
              initialValues={formValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                setFieldValue,
                isSubmitting,
                handleBlur,
              }) => (
                <Form
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <Row className="mb-3">
                    <Col md={6} className="mb-3">
                      <TextField
                        fullWidth
                        label="Intitulé *"
                        name="intitule"
                        value={values.intitule}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.intitule && Boolean(errors.intitule)}
                        helperText={touched.intitule && errors.intitule}
                        variant="outlined"
                      />
                    </Col>

                    <Col md={6} className="mb-3">
                      <TextField
                        fullWidth
                        label="Adresse *"
                        name="adresse"
                        value={values.adresse}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.adresse && Boolean(errors.adresse)}
                        helperText={touched.adresse && errors.adresse}
                        variant="outlined"
                        rows={2}
                      />
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6} className="mb-3">
                      <TextField
                        fullWidth
                        label="RCCM *"
                        name="rccm"
                        value={values.rccm}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.rccm && Boolean(errors.rccm)}
                        helperText={touched.rccm && errors.rccm}
                        variant="outlined"
                      />
                    </Col>

                    <Col md={6} className="mb-3">
                      <TextField
                        fullWidth
                        label="IFU *"
                        name="ifu"
                        value={values.ifu}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.ifu && Boolean(errors.ifu)}
                        helperText={touched.ifu && errors.ifu}
                        variant="outlined"
                      />
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6} className="mb-3">
                      <TextField
                        fullWidth
                        label="Téléphone *"
                        name="tel"
                        value={values.tel}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.tel && Boolean(errors.tel)}
                        helperText={touched.tel && errors.tel}
                        variant="outlined"
                        inputProps={{ maxLength: 15 }}
                      />
                    </Col>

                    <Col md={6} className="mb-3">
                      <TextField
                        fullWidth
                        label="Email *"
                        name="email"
                        type="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                        variant="outlined"
                      />
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6} className="mb-3">
                      <Switch
                        checked={values.isActive}
                        onChange={(e) =>
                          setFieldValue("isActive", e.target.checked)
                        }
                        name="isActive"
                        color="success"
                      />
                      <Typography variant="body1" component="span">
                        Actif
                      </Typography>
                    </Col>
                  </Row>

                  <Row className="mt-0 " style={{ marginTop: "auto" }}>
                    <Col xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 2,
                          pt: 2,
                          borderTop: "1px solid #eee",
                        }}
                      >
                        <Button
                          variant="outlined"
                          onClick={handleClose}
                          disabled={isSubmitting}
                          sx={{ px: 4 }}
                        >
                          Annuler
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={isSubmitting}
                          color="success"
                          sx={{ px: 4 }}
                          startIcon={
                            isSubmitting ? <CircularProgress size={20} /> : null
                          }
                        >
                          {isSubmitting ? "Enregistrement..." : "Enregistrer"}
                        </Button>
                      </Box>
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          )}
        </Modal.Body>
      </Paper>
    </Modal>
  );
};

export default UpdatestructureModal;
